import { queryOptions, useQuery } from '@tanstack/react-query';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { CloudIntegration } from '@cast/types-inventory';

export const CLOUD_ASSET_INTEGRATIONS_QUERY_KEY =
  'onboarding-discovered-environment-stats';

type CloudAssetIntegrationsQueryArgs = {
  organizationId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
};

const getCloudAssetIntegrationsQueryOptions = ({
  organizationId,
  enabled = true,
  refetchInterval = false,
}: CloudAssetIntegrationsQueryArgs) =>
  queryOptions({
    queryKey: [
      CLOUD_ASSET_INTEGRATIONS_QUERY_KEY,
      organizationId,
      'integrations',
    ] as const,
    queryFn: async (): Promise<CloudIntegration[]> => {
      const items: CloudIntegration[] = [];
      let pageCursor: string | undefined;

      do {
        const { data } = await apiClient.inventory.listCloudAssetIntegrations({
          organizationId: organizationId!,
          pageLimit: '1000',
          pageCursor,
        });
        items.push(...(data.items ?? []));
        pageCursor = data.nextPageCursor || undefined;
      } while (pageCursor);

      return items;
    },
    enabled: enabled && !!organizationId,
    refetchInterval,
  });

type UseCloudAssetIntegrationsQueryArgs = {
  enabled?: boolean;
  refetchInterval?: number | false;
};

export const useCloudAssetIntegrationsQuery = ({
  enabled = true,
  refetchInterval = false,
}: UseCloudAssetIntegrationsQueryArgs = {}) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getCloudAssetIntegrationsQueryOptions({
      organizationId: currentOrganization?.id,
      enabled,
      refetchInterval,
    })
  );
};
